import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, TableSkeleton, Amount, StatusDot,
  RowActions, RowAction, ListPagination, ListSearchInput, ListEmptyRow, btnPrimary, btnSecondary,
} from '@redpanda/ui';
import { useRefetchOnFocus } from '@redpanda/utils';
import { Payroll, usePayrollStoreBase } from '../stores/payrollStore';
import PayrollDialog from '../components/PayrollDialog';
import PayslipDialog from '../components/PayslipDialog';
import GeneratePayrollDialog from '../components/GeneratePayrollDialog';
import { useListSearch } from '../hooks/useListSearch';
import { notifyDataChanged } from '../utils/dataEvents';
import { pendingRowClass } from '../utils/pendingItem';
import { payrollTone, toneOf } from '../utils/statusTone';
import { getPayrolls } from '../services/api';

const COLUMNS = 9;

/** 薪資列表：金額欄靠右、狀態色點、點姓名看薪資單（D9） */
const PayrollListPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const store = usePayrollStoreBase();
  const { items: payrolls, pagination, isLoading, isPageLoading, showDeleteConfirmModal, isDeleting, fetchItems, setCurrentPage } = store;

  const [searchTerm, setSearchTerm] = useState('');
  const [dialogPayroll, setDialogPayroll] = useState<Payroll | null | undefined>(undefined); // undefined＝關閉、null＝新增
  const [payslip, setPayslip] = useState<Payroll | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);

  useEffect(() => {
    fetchItems(1);
  }, [fetchItems]);
  // 離開 30 秒以上切回分頁時重抓（R33）
  useRefetchOnFocus(() => usePayrollStoreBase.getState().refreshData());

  const search = searchTerm.trim();
  const searchResult = useListSearch<Payroll>('payrolls', search ? { search } : null, page => getPayrolls(page, { search }));
  const listItems = searchResult.active ? searchResult.items : payrolls;
  const listPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;
  const changePage = (page: number) => {
    if (searchResult.active) return searchResult.setPage(page);
    setCurrentPage(page);
    fetchItems(page);
  };

  const handleConfirmDelete = async () => {
    await store.confirmDelete();
    notifyDataChanged('payrolls');
  };

  const addButton = (
    <button type="button" onClick={() => setDialogPayroll(null)} className={btnPrimary}>
      {t('pages:payroll.list.addButton')}
    </button>
  );

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('pages:payroll.list.title')}</h1>
          <div className="flex space-x-3 whitespace-nowrap">
            {/* 月結：依薪資設定一次產生全部員工的草稿；單筆補登用「新增薪資」 */}
            <button type="button" onClick={() => setGenerateOpen(true)} className={btnSecondary}>{t('pages:payroll.generate.button')}</button>
            {addButton}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('pages:payroll.list.searchPlaceholder')} />
      </div>

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {(isLoading && payrolls.length === 0 && !searchResult.active) || listLoading ? (
        <TableSkeleton rows={10} columns={COLUMNS} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '1040px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-36">{t('translation:common.table.columns.name')}</TableHead>
                  <TableHead numeric className="w-28">{t('pages:payroll.list.period')}</TableHead>
                  <TableHead numeric>{t('pages:payroll.form.baseSalary')}</TableHead>
                  <TableHead numeric>{t('pages:payroll.list.otherIncome')}</TableHead>
                  <TableHead numeric>{t('pages:payroll.list.insurance')}</TableHead>
                  <TableHead numeric>{t('pages:payroll.form.otherDeductions')}</TableHead>
                  <TableHead numeric>{t('pages:payroll.form.netSalary')}</TableHead>
                  <TableHead className="w-28 !pl-8">{t('translation:common.table.columns.status')}</TableHead>
                  <TableHead className="w-32">{t('translation:common.table.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listItems.length === 0 ? (
                  <ListEmptyRow
                    colSpan={COLUMNS}
                    filtered={!!search}
                    title={t('pages:payroll.list.noPayrolls')}
                    hint={t('pages:payroll.list.emptyHint')}
                    action={<button type="button" onClick={() => setGenerateOpen(true)} className={btnPrimary}>{t('pages:payroll.generate.button')}</button>}
                  />
                ) : (
                  listItems.map(payroll => (
                    <TableRow key={payroll.id} className={pendingRowClass(payroll)}>
                      <TableCell className="truncate">
                        {/* D9：點姓名看薪資單 */}
                        <button
                          type="button"
                          onClick={() => setPayslip(payroll)}
                          className="font-medium text-fg hover:text-accent hover:underline truncate max-w-full text-left"
                          title={t('pages:payroll.list.viewPayslip')}
                        >
                          {payroll.employee?.name || '—'}
                        </button>
                      </TableCell>
                      <TableCell numeric>{payroll.periodYear}/{String(payroll.periodMonth).padStart(2, '0')}</TableCell>
                      <TableCell numeric><Amount value={payroll.baseSalary} /></TableCell>
                      <TableCell numeric><Amount value={(payroll.allowances || 0) + (payroll.bonuses || 0) + (payroll.overtimePay || 0)} /></TableCell>
                      <TableCell numeric><Amount value={(payroll.laborInsurance || 0) + (payroll.healthInsurance || 0) + (payroll.laborPension || 0)} /></TableCell>
                      <TableCell numeric><Amount value={payroll.otherDeductions || 0} /></TableCell>
                      <TableCell numeric className="font-medium"><Amount value={payroll.netSalary} /></TableCell>
                      <TableCell className="!pl-8">
                        <StatusDot tone={toneOf(payrollTone, payroll.status)} label={t(`pages:payroll.status.${payroll.status}`)} />
                      </TableCell>
                      <TableCell>
                        <RowActions>
                          <RowAction onClick={() => setDialogPayroll(payroll)}>{t('translation:common.actions.edit')}</RowAction>
                          {payroll.status !== 'PAID' && (
                            <RowAction tone="danger" onClick={() => store.handleDelete(payroll.id)}>{t('translation:common.actions.delete')}</RowAction>
                          )}
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={store.closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title={t('pages:payroll.list.confirmDeleteTitle')}
        message={t('pages:payroll.list.confirmDeleteMessage')}
        confirmText={t('translation:common.actions.delete')}
        cancelText={t('translation:common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('translation:common.status.deleting')}
        t={t}
      />

      {dialogPayroll !== undefined && (
        <PayrollDialog payroll={dialogPayroll ?? undefined} onClose={() => setDialogPayroll(undefined)} />
      )}
      {payslip && <PayslipDialog payroll={payslip} onClose={() => setPayslip(null)} />}
      {generateOpen && <GeneratePayrollDialog onClose={() => setGenerateOpen(false)} />}
    </div>
  );
};

export default PayrollListPage;
